import { tokens } from "@/tokens";
import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Collapse,
  Stack,
  Badge,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { ValidationIssue } from './validation-utils';
import ExpandedCertificateForm from './expanded-certificate-form';

interface CertificateReviewCardProps {
  section: 'certifier' | 'exporter' | 'producer' | 'importer' | 'items';
  title: string;
  data: CertificateExtractedData;
  onUpdate: (updatedData: CertificateExtractedData) => void;
  expanded: boolean;
  onToggle: () => void;
  formType: string;
  validationIssues: ValidationIssue[];
}

const CertificateReviewCard: React.FC<CertificateReviewCardProps> = ({
  section,
  title,
  data,
  onUpdate,
  expanded,
  onToggle,
  formType,
  validationIssues,
}) => {
  const hasErrors = validationIssues.some(issue => issue.type === 'error');
  const hasWarnings = validationIssues.some(issue => issue.type === 'warning');
  const issueCount = validationIssues.length;

  const getStatusColor = () => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  // Type guard to check if details are CertifierDetails
  const isCertifierDetails = (details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails): details is CertifierDetails => {
    return 'yourName' in details || 'title' in details;
  };

  const getSummaryText = () => {
    if (section === 'certifier') {
      const hasDetails = data.certifierDetails && isCertifierDetails(data.certifierDetails) && data.certifierDetails.yourName;
      return hasDetails ? data.certifierDetails?.yourName : 'No details provided (optional)';
    }
    
    if (section === 'exporter') {
      const hasDetails = data.exporterDetails && data.exporterDetails.companyName;
      return hasDetails ? 'Details provided (click to expand)' : 'No details provided (optional)';
    }
    
    if (section === 'producer') {
      const hasDetails = data.producerDetails && data.producerDetails.companyName;
      return hasDetails ? 'Details provided (click to expand)' : 'No details provided (optional)';
    }
    
    if (section === 'importer') {
      const hasDetails = data.importerDetails && data.importerDetails.companyName;
      return hasDetails ? 'Details provided (click to expand)' : 'No details provided (optional)';
    }
    
    if (section === 'items') {
      const itemCount = data.items?.length || 0;
      const includedCount = data.items?.filter(item => item.INCLUDED).length || 0;
      const excludedCount = itemCount - includedCount;
      return `${itemCount} total items (${includedCount} included, ${excludedCount} excluded)`;
    }
    
    return 'No details provided (optional)';
  };

  return (
    <Card 
      sx={{ 
        border: hasErrors ? '2px solid #f44336' : '1px solid #e0e0e0',
      }}
    >
      <CardContent 
        sx={{ 
          pb: 1,
          '&:hover': {
            backgroundColor: tokens.bg.hover,
            cursor: 'pointer'
          }
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6" component="div">
                {title}
              </Typography>
              {hasErrors && (
                <Chip
                  size="small"
                  label="Has Errors"
                  color="error"
                  icon={<ErrorIcon fontSize="small" />}
                />
              )}
              {hasWarnings && !hasErrors && (
                <Chip
                  size="small"
                  label="Has Warnings"
                  color="warning"
                  icon={<WarningIcon fontSize="small" />}
                />
              )}
            </Stack>
            
            <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 1 }}>
              {getSummaryText()}
            </Typography>
          </Box>

          <Badge badgeContent={issueCount} color={getStatusColor()} invisible={issueCount === 0}>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Badge>
        </Box>
      </CardContent>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0, borderTop: '1px solid #e0e0e0' }}>
          <ExpandedCertificateForm
            section={section}
            data={data}
            onUpdate={onUpdate}
            formType={formType}
            validationIssues={validationIssues}
          />
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default CertificateReviewCard;